// UPDATED VERSION - Enhanced Colleges Management with All Fields
import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import DataTable from '../../components/admin/DataTable';
import ImageUpload from '../../components/admin/ImageUpload';
import RichTextEditor from '../../components/admin/RichTextEditor';
import LabsManager from '../../components/admin/LabsManager';

const CollegesManagement = () => {
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name_ar: '', name_en: '', 
    description_ar: '', description_en: '',
    about_ar: '', about_en: '',
    vision_ar: '', vision_en: '',
    mission_ar: '', mission_en: '',
    objectives_ar: '', objectives_en: '',
    dean_name_ar: '', dean_name_en: '',
    dean_title_ar: '', dean_title_en: '',
    dean_message_ar: '', dean_message_en: '',
    dean_image: '',
    image: '',
    cover_image: '',
    is_published: true,
    order: 0
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => { fetchColleges(); }, []);

  const fetchColleges = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/colleges/all`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      setColleges(await response.json());
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = editing
      ? `${BACKEND_URL}/api/colleges/${editing}`
      : `${BACKEND_URL}/api/colleges/`;
    const method = editing ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        fetchColleges();
        resetForm();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleEdit = (college) => {
    setEditing(college.id);
    setFormData(college);
    setShowForm(true);
  };

  const handleDelete = async (college) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الكلية؟')) return;
    try {
      await fetch(`${BACKEND_URL}/api/colleges/${college.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      fetchColleges();
    } catch (error) {
      console.error(error);
    }
  };

  const resetForm = () => {
    setFormData({
      name_ar: '', name_en: '', 
      description_ar: '', description_en: '',
      about_ar: '', about_en: '',
      vision_ar: '', vision_en: '',
      mission_ar: '', mission_en: '',
      objectives_ar: '', objectives_en: '',
      dean_name_ar: '', dean_name_en: '',
      dean_title_ar: '', dean_title_en: '',
      dean_message_ar: '', dean_message_en: '',
      dean_image: '',
      image: '',
      cover_image: '',
      is_published: true,
      order: 0
    });
    setEditing(null);
    setShowForm(false);
  };

  const columns = [
    { accessor: 'name_ar', header: 'اسم الكلية' },
    { accessor: 'dean_name_ar', header: 'العميد' },
    { accessor: 'is_published', header: 'منشور', render: (row) => row.is_published ? 'نعم' : 'لا' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-[#0F388E]">إدارة الكليات</h1>
        <Button onClick={() => setShowForm(true)} className="bg-[#0F388E]">
          <Plus className="ml-2 w-5 h-5" />
          إضافة كلية
        </Button>
      </div>

      {!showForm && (
        <DataTable
          data={colleges}
          columns={columns}
          onEdit={handleEdit}
          onDelete={handleDelete}
          loading={loading}
        />
      )}

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editing ? 'تعديل' : 'إضافة'} كلية</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg space-y-4 border border-blue-100">
                <h3 className="font-bold text-[#0F388E] text-lg flex items-center gap-2">
                  <span className="bg-[#0F388E] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">1</span>
                  المعلومات الأساسية
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="اسم الكلية بالعربي *"
                    value={formData.name_ar}
                    onChange={(e) => setFormData({...formData, name_ar: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                    required
                  />
                  <input
                    type="text"
                    placeholder="College Name in English"
                    value={formData.name_en}
                    onChange={(e) => setFormData({...formData, name_en: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                  />
                </div>
              </div>

              {/* Description */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg space-y-4 border border-green-100">
                <h3 className="font-bold text-[#0F388E] text-lg flex items-center gap-2">
                  <span className="bg-[#0F388E] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">2</span>
                  الوصف المختصر
                </h3>
                <RichTextEditor
                  label="الوصف بالعربي *"
                  value={formData.description_ar}
                  onChange={(v) => setFormData({...formData, description_ar: v})}
                  rows={3}
                />
                <RichTextEditor
                  label="Description in English"
                  value={formData.description_en}
                  onChange={(v) => setFormData({...formData, description_en: v})}
                  rows={3}
                />
              </div>

              {/* About College */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg space-y-4 border border-purple-100">
                <h3 className="font-bold text-[#0F388E] text-lg flex items-center gap-2">
                  <span className="bg-[#0F388E] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">3</span>
                  نبذة عن الكلية
                </h3>
                <RichTextEditor
                  label="نبذة عن الكلية بالعربي"
                  value={formData.about_ar}
                  onChange={(v) => setFormData({...formData, about_ar: v})}
                  rows={5}
                />
                <RichTextEditor
                  label="About the College in English"
                  value={formData.about_en}
                  onChange={(v) => setFormData({...formData, about_en: v})}
                  rows={5}
                />
              </div>

              {/* Vision, Mission, Objectives */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-6 rounded-lg space-y-4 border border-amber-100">
                <h3 className="font-bold text-[#0F388E] text-lg flex items-center gap-2">
                  <span className="bg-[#0F388E] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">4</span>
                  الرؤية والرسالة والأهداف
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <RichTextEditor
                    label="الرؤية بالعربي"
                    value={formData.vision_ar}
                    onChange={(v) => setFormData({...formData, vision_ar: v})}
                    rows={4}
                  />
                  <RichTextEditor
                    label="Vision in English"
                    value={formData.vision_en}
                    onChange={(v) => setFormData({...formData, vision_en: v})}
                    rows={4}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <RichTextEditor
                    label="الرسالة بالعربي"
                    value={formData.mission_ar}
                    onChange={(v) => setFormData({...formData, mission_ar: v})}
                    rows={4}
                  />
                  <RichTextEditor
                    label="Mission in English"
                    value={formData.mission_en}
                    onChange={(v) => setFormData({...formData, mission_en: v})}
                    rows={4}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <RichTextEditor
                    label="الأهداف بالعربي"
                    value={formData.objectives_ar}
                    onChange={(v) => setFormData({...formData, objectives_ar: v})}
                    rows={4}
                  />
                  <RichTextEditor
                    label="Objectives in English"
                    value={formData.objectives_en}
                    onChange={(v) => setFormData({...formData, objectives_en: v})}
                    rows={4}
                  />
                </div>
              </div>

              {/* Dean Information */}
              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-6 rounded-lg space-y-4 border border-cyan-100">
                <h3 className="font-bold text-[#0F388E] text-lg flex items-center gap-2">
                  <span className="bg-[#0F388E] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">5</span>
                  معلومات عميد الكلية
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="اسم العميد بالعربي"
                    value={formData.dean_name_ar}
                    onChange={(e) => setFormData({...formData, dean_name_ar: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                  />
                  <input
                    type="text"
                    placeholder="Dean Name in English"
                    value={formData.dean_name_en}
                    onChange={(e) => setFormData({...formData, dean_name_en: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                  />
                  <input
                    type="text"
                    placeholder="لقب العميد (مثال: عميد الكلية)"
                    value={formData.dean_title_ar}
                    onChange={(e) => setFormData({...formData, dean_title_ar: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                  />
                  <input
                    type="text"
                    placeholder="Dean Title (e.g., Dean of College)"
                    value={formData.dean_title_en}
                    onChange={(e) => setFormData({...formData, dean_title_en: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                  />
                </div>
                <RichTextEditor
                  label="كلمة العميد بالعربي"
                  value={formData.dean_message_ar}
                  onChange={(v) => setFormData({...formData, dean_message_ar: v})}
                  rows={5}
                />
                <RichTextEditor
                  label="Dean's Message in English"
                  value={formData.dean_message_en}
                  onChange={(v) => setFormData({...formData, dean_message_en: v})}
                  rows={5}
                />
                <ImageUpload
                  label="صورة العميد"
                  value={formData.dean_image}
                  onChange={(v) => setFormData({...formData, dean_image: v})}
                />
              </div>

              {/* Labs Manager */}
              {editing && (
                <LabsManager collegeId={editing} />
              )}

              {/* Images */}
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-6 rounded-lg space-y-4 border border-indigo-100">
                <h3 className="font-bold text-[#0F388E] text-lg flex items-center gap-2">
                  <span className="bg-[#0F388E] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">6</span>
                  صور الكلية
                </h3>
                <ImageUpload
                  label="شعار/صورة الكلية"
                  value={formData.image}
                  onChange={(v) => setFormData({...formData, image: v})}
                />
                <ImageUpload
                  label="صورة الغلاف (Cover Image)"
                  value={formData.cover_image}
                  onChange={(v) => setFormData({...formData, cover_image: v})}
                />
              </div>

              {/* Save Buttons */}
              <div className="flex gap-3 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={resetForm}>
                  إلغاء
                </Button>
                <Button type="submit" className="bg-[#0F388E]">
                  {editing ? 'تحديث الكلية' : 'حفظ الكلية'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CollegesManagement;
